/** Login (ST-01) — branded dark screen; Supabase email+password, route by role. */
import { getClaims } from '@gym/api-client';
import { Dumbbell, Smartphone } from 'lucide-react';
import type { FormEvent, ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/ui/Button.js';
import { Field, Input } from '../components/ui/Field.js';
import { LocaleSwitcher } from '../components/ui/LocaleSwitcher.js';
import { supabase } from '../lib/supabase.js';

export function LoginPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Demo credentials pre-filled so anyone can sign in with one click on the public demo.
  const [email, setEmail] = useState('owner@flexgym.mk');
  const [password, setPassword] = useState('GymDemo123!');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      setBusy(false);
      setError(t('auth.invalidCredentials'));
      return;
    }
    // Route by verified role; everyone lands on the dashboard.
    await getClaims(supabase);
    setBusy(false);
    navigate('/dashboard', { replace: true });
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-surface p-10 lg:flex">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(60% 50% at 20% 10%, rgba(249,115,22,0.18), transparent 60%), radial-gradient(50% 50% at 90% 90%, rgba(34,197,94,0.14), transparent 60%)',
          }}
          aria-hidden
        />
        <div className="relative flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-black">
            <Dumbbell className="h-6 w-6" aria-hidden />
          </span>
          <div className="leading-none">
            <p className="font-display text-2xl font-bold tracking-tight text-fg">FlexGym</p>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted">
              {t('auth.tagline')}
            </p>
          </div>
        </div>
        <div className="relative max-w-md">
          <h2 className="font-display text-4xl font-bold leading-tight text-fg">
            {t('auth.heroTitle')}
          </h2>
          <p className="mt-3 text-muted">{t('auth.heroSubtitle')}</p>
        </div>
        <p className="relative text-xs text-faint">© FlexGym · Skopje, MK</p>
      </div>

      {/* Form panel */}
      <div className="relative flex items-center justify-center bg-bg px-6 py-10">
        <div className="absolute right-5 top-5">
          <LocaleSwitcher />
        </div>
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center gap-2.5 lg:hidden">
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary text-black">
              <Dumbbell className="h-5 w-5" aria-hidden />
            </span>
            <span className="font-display text-xl font-bold text-fg">FlexGym</span>
          </div>
          <h1 className="font-display text-2xl font-bold text-fg">{t('auth.signIn')}</h1>
          <p className="mt-1 text-sm text-muted">{t('auth.signInHint')}</p>

          <form onSubmit={onSubmit} className="mt-6 grid gap-4">
            <Field label={t('auth.email')}>
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  placeholder="owner@flexgym.mk"
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('auth.password')} error={error ?? undefined}>
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  placeholder="••••••••"
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
            <Button type="submit" loading={busy} className="mt-1 w-full">
              {t('auth.signIn')}
            </Button>
          </form>

          {/* Public demo: credentials are pre-filled; offer a one-tap path to the member app. */}
          <p className="mt-4 rounded-lg border border-border bg-surface-2/50 px-3 py-2 text-xs text-muted">
            Demo — credentials are pre-filled, just press <span className="text-fg">Sign in</span>.
          </p>
          <a
            href="/app/"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-border px-3 py-2.5 text-sm font-medium text-fg transition-colors hover:bg-surface-2"
          >
            <Smartphone className="h-4 w-4 text-primary" aria-hidden />
            View the member app
          </a>
        </div>
      </div>
    </div>
  );
}
